/**
 * WebDataCarousel — web port of `apps/WunderGuide/views/Dashboard.tsx`'s
 * carousel block (driven by `audioChat.onCarouselItemsUpdate` / `webData`
 * socket events).
 *
 * Each item is `{ id, src: { uri?, html? }, updatedAt }`. We render a
 * paginated horizontal strip of iframes (RN uses react-native-reanimated-carousel
 * + WebView; web uses scroll-snap + iframe).
 *
 * Floating expand/collapse button in bottom-right mirrors RN's
 * `floatingCarouselButton`.
 */
import React, { useRef } from 'react';
import WebView from '../../common/WebView';
import './WebDataCarousel.css';

export interface WebDataItem {
  id: string;
  src: { uri?: string; html?: string };
  updatedAt: number;
}

interface WebDataCarouselProps {
  items: WebDataItem[];
  minimized: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  onRemove: (id: string) => void;
}

const WebDataCarousel: React.FC<WebDataCarouselProps> = ({
  items,
  minimized,
  onMinimize,
  onExpand,
  onRemove,
}) => {
  const stripRef = useRef<HTMLDivElement | null>(null);

  if (items.length === 0) return null;

  return (
    <>
      {/* Expanded carousel sheet */}
      {!minimized && (
        <div className="wg-wd-carousel" role="region" aria-label="Live cards">
          <div className="wg-wd-carousel__strip" ref={stripRef}>
            {items.map(item => (
              <div className="wg-wd-card" key={item.id}>
                <div className="wg-wd-card__actions">
                  <button
                    type="button"
                    className="wg-wd-card__action"
                    aria-label="Delete card"
                    onClick={() => onRemove(item.id)}
                  >
                    <TrashIcon />
                  </button>
                  <button
                    type="button"
                    className="wg-wd-card__action"
                    aria-label="Minimize"
                    onClick={onMinimize}
                  >
                    <ContractIcon />
                  </button>
                </div>
                <WebView
                  title={`card-${item.id}`}
                  source={
                    item.src.uri
                      ? { uri: item.src.uri }
                      : { html: item.src.html || '' }
                  }
                  sandbox={
                    item.src.uri
                      ? 'allow-same-origin allow-scripts allow-popups allow-forms'
                      : 'allow-same-origin'
                  }
                />
              </div>
            ))}
          </div>
          <div className="wg-wd-carousel__count" aria-hidden>
            {items.length}
          </div>
        </div>
      )}

      {/* Floating expand button (always visible while items exist) */}
      <button
        type="button"
        className="wg-wd-fab"
        onClick={minimized ? onExpand : onMinimize}
        aria-label={minimized ? 'Expand cards' : 'Minimize cards'}
      >
        {minimized ? <ExpandIcon /> : <ContractIcon />}
        {minimized && <span className="wg-wd-fab__badge">{items.length}</span>}
      </button>
    </>
  );
};

export default WebDataCarousel;

function TrashIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ContractIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 14h6v6" />
      <path d="M20 10h-6V4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}
